/**
 * Хук данных экрана «Аналитика заявок» (manager v3 §6.3).
 *
 * Бэк: GET /v1/requests/analytics?period=day|week|month|custom&from=&to=
 * (internal/request, handlers_analytics.RequestsAnalytics) — РЕАЛЬНЫЕ
 * агрегаты по service_requests за период: 8 KPI + разбивка по отделам +
 * топ-исполнители. RBAC: manager/owner (гость 403).
 *
 * Server-state только через React Query. Период — параметр запроса (ключ
 * входит в queryKey, поэтому смена периода триггерит refetch).
 */
import { useQuery } from '@tanstack/react-query';
import { customFetch } from '@hsc/api-client';

export type AnalyticsPeriodKey = 'day' | 'week' | 'month' | 'custom';

export interface AnalyticsPeriod {
  key: AnalyticsPeriodKey;
  from: string;
  to: string;
  label: string;
}

export interface AnalyticsDept {
  department: string;
  count: number;
  avg_min: number;
  sla_compliance_pct: number | null;
}

export interface AnalyticsPerformer {
  user_id: string;
  name: string;
  count: number;
  avg_min: number;
}

export interface RequestsAnalytics {
  period: AnalyticsPeriod;
  total: number;
  sla_compliance_pct: number;
  avg_response_min: number;
  breaches: number;
  p50_min: number;
  p95_min: number;
  mttr_min: number;
  throughput_per_hour: number;
  resolved: number;
  sla_measured: number;
  departments: AnalyticsDept[];
  performers: AnalyticsPerformer[];
}

export interface CustomRange {
  from: string;
  to: string;
}

export function useRequestsAnalytics(period: AnalyticsPeriodKey, custom?: CustomRange) {
  const params: Record<string, string> =
    period === 'custom' && custom ? { from: custom.from, to: custom.to } : { period };
  return useQuery<RequestsAnalytics>({
    queryKey: ['requests', 'analytics', period, custom?.from ?? '', custom?.to ?? ''],
    queryFn: () =>
      customFetch<RequestsAnalytics>({
        url: '/requests/analytics',
        method: 'GET',
        params,
      }),
    staleTime: 60_000,
  });
}

/**
 * Экспорт отчёта в CSV (кнопка «Экспорт»). Тот же эндпойнт с format=csv;
 * тянем текст через customFetch (несёт Bearer+X-Tenant-Slug, простой
 * <a href> их бы потерял), затем триггерим скачивание Blob'ом.
 */
export async function downloadAnalyticsCsv(
  period: AnalyticsPeriodKey,
  custom?: CustomRange,
): Promise<void> {
  const params: Record<string, string> =
    period === 'custom' && custom
      ? { format: 'csv', from: custom.from, to: custom.to }
      : { format: 'csv', period };
  const csv = await customFetch<string>({ url: '/requests/analytics', method: 'GET', params });
  if (typeof document === 'undefined') return;
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `requests-analytics-${period}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
